import { useMutation } from '@apollo/client';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
// @ts-expect-error Leftover after Brewery migration
import { FILE_TOO_LARGE, TOO_MANY_FILES } from 'react-dropzone/src/utils';
import { FormattedMessage } from 'react-intl';
import { useEditor } from 'slate-react';

import { useBanner } from '@xing-com/banner';
import {
  uploadImage,
  dataURLtoBlob,
} from '@xing-com/content-editor-plugin-helpers';
import { ToolbarButtonComponent } from '@xing-com/content-editor-toolbar';

import { ACCEPTED_FILES, MAX_SIZE } from './constants';
import { UploadImageDocument } from './upload-image.gql-types';
import { insertImage } from './utils';

type ImageUploadProps = {
  isDisabled: boolean;
  isHighlighted: boolean;
  icon: React.ReactNode;
  label: string;
  id?: string;
};

const ImageUpload = ({
  isDisabled,
  isHighlighted,
  icon,
  label,
  id,
}: ImageUploadProps): React.ReactNode => {
  const { showBanner } = useBanner();
  const editor = useEditor();
  const [imageUrl, setImageUrl] = React.useState('');
  const [uploadRequest] = useMutation(UploadImageDocument, {
    onError: () => {
      showBanner({
        error: true,
        children: (
          <FormattedMessage id="CONTENT_EDITOR_IMAGE_UPLOAD_ERROR_GRAPHQL" />
        ),
      });
    },
    onCompleted: (data) => {
      if (data?.uploadRequest?.success) {
        const {
          uploadRequest: {
            success: { id, authToken, url },
          },
        } = data;

        // @ts-expect-error Leftover after Brewery migration
        dataURLtoBlob(imageUrl).then((file) => {
          uploadImage(url, authToken, file)
            .then(() => {
              insertImage(editor, imageUrl, id);
            })
            .catch(() =>
              showBanner({
                error: true,
                children: (
                  <FormattedMessage id="CONTENT_EDITOR_IMAGE_UPLOAD_ERROR" />
                ),
              })
            );
        });
      } else {
        showBanner({
          error: true,
          children: (
            <FormattedMessage id="CONTENT_EDITOR_IMAGE_UPLOAD_ERROR_GRAPHQL" />
          ),
        });
      }
    },
  });

  // @ts-expect-error Leftover after Brewery migration
  const onDrop = React.useCallback((acceptedFiles) => {
    // @ts-expect-error Leftover after Brewery migration
    acceptedFiles.forEach((file) => {
      const { name, type, size } = file;
      const preview = URL.createObjectURL(file);
      setImageUrl(preview);
      uploadRequest({
        variables: {
          fileName: name,
          fileSize: size,
          fileType: type,
        },
      });
    });
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize: MAX_SIZE,
    noDrag: true,
    onDropRejected: (fileRejections) => {
      const tooManyFiles: boolean = fileRejections.some(({ errors }) =>
        errors.some((e) => e.code.includes(TOO_MANY_FILES))
      );
      const tooLarge: boolean = fileRejections.some(({ errors }) =>
        errors.some((e) => e.code.includes(FILE_TOO_LARGE))
      );

      if (tooManyFiles) {
        showBanner({
          error: true,
          children: (
            <FormattedMessage id="CONTENT_EDITOR_IMAGE_UPLOAD_ERROR_MULTIPLE" />
          ),
        });
      } else if (tooLarge) {
        showBanner({
          error: true,
          children: (
            <FormattedMessage id="CONTENT_EDITOR_IMAGE_UPLOAD_ERROR_TOO_LARGE" />
          ),
        });
      } else {
        showBanner({
          error: true,
          children: (
            <FormattedMessage id="CONTENT_EDITOR_IMAGE_UPLOAD_ERROR_PROCESSING" />
          ),
        });
      }
    },
  });

  return (
    <div {...getRootProps()}>
      <input
        aria-label={label}
        {...getInputProps()}
        disabled={isDisabled}
        accept={ACCEPTED_FILES}
      />
      <ToolbarButtonComponent
        aria-label={label}
        disabled={isDisabled}
        // @ts-expect-error ???
        icon={icon}
        id={id}
        size="medium"
        text={!isHighlighted}
      />
    </div>
  );
};

export { ImageUpload };
